import { sampleAccount, sampleFindings, sampleQuestions } from './studioSamples';
import { samplePerformance, samplePersonality, sampleProfileVideos, sampleWords } from './personalitySamples';
import { buildAnswer, missingRequired, onboardingProfile } from './onboardingRules';
import { createArtRoutes, ART_IMAGE_MAX_BYTES, ART_IMAGE_MIME_TYPES, ART_VIDEO_MAX_BYTES, ART_VIDEO_MIME_TYPES } from './studioArt.fake';
import { createAutomationRoutes, emptyDirectMessages } from './studioAutomations.fake';

// In-memory double of `studioApi`: the hook tests use it directly and `?mock=true`
// wraps it (studioApi.mock.js). It keeps the core-platform rules the web relies
// on: 404 `creator_not_initialized` before the account exists, every Instagram profile a
// separate workspace (contract 2.0.0: the account is `pending` until the @ onboarding
// connects its first profile; projects, memories, topics and prompt audio need the
// `profile_id` of a connected profile), chat = project (`stc-` id over the same row), one active run,
// quote before run, version checks and `revision_conflict`, and audio in either
// scope (contract 1.5.0: without a project it waits in the creator scope until
// `createProject` adopts its reviewed transcription). Payments (contract 1.8.0): the
// catalog, the subscription and checkouts, fulfilled only when the "webhook"
// (`server.fulfillCheckout`) runs; the plan allowance is spent before purchased credits.
// Personality (contract 2.3.0; `features.personality`, else 403 `personality_unavailable`): each
// profile's Reels with what makes one selectable, a `personality` quote for 3 of them, one
// mapping per profile at a time (played by `server.advancePersonality`/`completePersonality`/
// `failPersonality`, where a failed video fails alone), and the active personality.
// Refine suggestions (contract 2.4.0): an AI revision carries the ones its run wrote
// (`server.succeed`), a manual save keeps the base revision's. What works for the creator
// (contract 2.5.0): with `db.performance` set, a personality quote also samples the profile's
// best, middle and worst reels by reach (and costs more), the mapping compares them
// (`comparing`) and the personality carries `performance`; null keeps the comparison off.
// Images in the arts (contract 2.9.0, studioArt.fake.js): every quote carries `action_id` (null
// but on an `image` quote) and every action `images` and `preview` (null but on an image
// question); `features.art_image_generation` and `features.community_images` start off.
const clone =value => (value === undefined ? undefined : JSON.parse(JSON.stringify(value)));
const now = () => new Date().toISOString();
const stamps = () => ({ created: now(), updated: now(), deleted: null });
const page = (data, perPage = 50) => ({ current_page: 1, per_page: perPage, total: data.length, last_page: 1, data });
const later = ms => new Date(Date.now() + ms).toISOString();
// `STUDIO_QUOTE_TTL_SECONDS` and the presigned PUT's validity in core.
const QUOTE_TTL_MS = 900e3;
const UPLOAD_TTL_MS = 900e3;
// A connected profile's dates: opened when the onboarding found the @, connected when it completed.
const profileStamps = (created = now()) => ({ created, updated: now(), connected: now() });
// The automatic snapshot a ranking is served from; before the first one, `unavailable`.
const topicSnapshot = topics => topics.find(topic => topic.snapshot)?.snapshot
  || { id: null, version: null, state: 'unavailable', freshness: 'unavailable', sources: [], timestamps: { generated: null, valid_until: null, observed: now() } };
const ACTIVE = ['queued', 'running', 'waiting_user'];
// Contract 1.7.0 (policy 2026-10-v3): a quote reserves the ceiling the creator consents to;
// a successful operation is charged what its model calls cost plus the 70% margin
// (R$ 1,00 = 50 credits) and the rest of the reservation comes back.
const POLICY = '2026-10-v3';
const CEILINGS = { generate: 170, revise: 200, transcribe: 50 };
// The balance an operation needs to start; below its ceiling the whole balance is reserved.
const MINIMUMS = { generate: 30, revise: 35, transcribe: 15 };
const settle = (db, id, cost) => { db.credits += Math.max(0, (db.charges[id] || 0) - cost); delete db.charges[id]; };
const HANDLE = /^[A-Za-z0-9._]{1,30}$/;
// Contract 1.8.0 catalog (minor units). Plan credits do not roll over; pack credits do.
const PRICES = amount => ['brl', 'usd', 'eur'].map(currency => ({ currency, unit_amount: amount }));
const PLANS = [
  { plan: 'free', rank: 0, monthly_credits: 0, interval: null, features: { trending_topics: false }, prices: [] },
  { plan: 'standard', rank: 1, monthly_credits: 5000, interval: 'month', features: { trending_topics: false }, prices: PRICES(7990) },
  { plan: 'pro', rank: 2, monthly_credits: 15000, interval: 'month', features: { trending_topics: true }, prices: PRICES(18990) },
];
const CREDIT_PACK = { credits_per_pack: 50, min_quantity: 10, max_quantity: 999, presets: [10, 20, 50, 100], prices: PRICES(100) };
const planOf = sku => PLANS.find(item => item.plan === sku);
const amountIn = (prices, currency) => prices.find(price => price.currency === currency)?.unit_amount ?? null;
const MONTH_MS = 30 * 864e5;
// Personality: `limits.personality_*`, and the quote of a mapping (core §8): the minutes of the
// videos not transcribed before at the transcription price, plus the analysis ceiling. To start
// it needs the transcription plus the analysis minimum (D1); the rest of the ceiling is optional.
// A transcript under `minWords` makes the video unselectable for good (`insufficient_speech`).
const PERSONALITY = { videos: 3, min: 10, max: 300, minWords: 15 };
// Failures that may pass with the same video on a new mapping.
const RETRYABLE = ['download_failed', 'provider_unavailable'];
const TRANSCRIPTION_CREDITS_PER_MINUTE = 5.4;
const PERSONALITY_ANALYSIS = { ceiling: 80, minimum: 50 };
const MAPPING_RUNNING = ['queued', 'running'];
// 2.5.0, core PerformanceSampler: the latest reels older than 72 h ranked by views; 13 at
// least, 3 standouts (1.5× the median) among the top third, then 5 best, 3 middle and 5
// worst that can be transcribed. The comparison call adds its own ceiling and minimum.
const PERFORMANCE = { recent: 100, minRanked: 13, minTier: 3, minAgeMs: 72 * 3600e3, ratio: 1.5, top: 5, middle: 3, bottom: 5, ceiling: 80, minimum: 40, cost: 31 };
const SAMPLED = ['ready', 'no_pattern'];
// The signed copy of a mapped video's cover expires; the double only dates it. The listing's
// cover is the Meta CDN URL itself, never stored, so it carries no expiry (`expires: null`).
const MEDIA_TTL_MS = 3600e3;
// Exports: the file each format must be (`limits.download_ttl_seconds` dates its link).
const EXPORT_FILES = { pdf: ['application/pdf', 'pdf'], docx: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'docx'], png: ['image/png', 'png'], jpeg: ['image/jpeg', 'jpg'],
  mp4: ['video/mp4', 'mp4'] };
const DOWNLOAD_TTL_MS = 900e3;
const slug = value => (value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'studio';

export function createFakeStudioApi() {
  let db;
  let sequence = 0;
  const calls = [];
  const nextId = prefix => `${prefix}-${(++sequence).toString(36).padStart(7, '0')}`;
  const reset = () => {
    sequence = 0; calls.length = 0;
    db = { creator: null, projects: [], messages: [], revisions: [], runs: [], memories: [], quotes: [], topics: [], assets: [], transcriptions: [], credits: 250, charges: {}, available: true, offline: false,
      // Onboarding: what the platform lookup answers (`found`, `not_found`, `unavailable`,
      // `rate_limited`) and whether the analysis yields findings (`findings`, `none`).
      onboarding: null, onboardingPlatforms: ['instagram'], lookup: 'found', analysis: 'findings', contract: '2.13.0', maxProfiles: 5,
      // Payments: `purchased` is the share of the balance that never expires (initial grant
      // and packs), `currency` is fixed by the first purchase, `upgrade` is how the next
      // plan change ends (`paid`, `requires_action`, `payment_failed`). `invoices` mirrors
      // what Stripe bills (subscription create/cycle/update and packs), oldest first.
      payments: { subscription: null, checkouts: [], paid: {}, purchased: 250, currency: null, upgrade: 'paid', invoices: [] },
      // Personality: `videos` per profile (read from Meta on first use), the mappings, the
      // active personality per profile, the words of every video already transcribed (reused,
      // never charged again) and what each `personality` quote was given for.
      videos: {}, personalityMappings: [], personalities: {}, transcribed: {}, personalityQuotes: {},
      // What works for the creator: the outcome the comparison reaches (`ready`, `no_pattern`,
      // `no_standouts`, `insufficient_videos`, `unavailable`; null = off, as before 2.5.0) and
      // the sample each mapping took from its quote.
      performance: null, performanceSamples: {},
      // Arts (contract 2.7.0 proposal, studioArt.fake.js): the art formats offered (none until
      // the mock turns them on), art revisions, each profile's brand kit, the signed URL of each
      // uploaded image and what each art run was asked to target. 2.9.0: the image question of
      // each art run that asked one, and what each `image` quote was given for.
      artFormats: [], artRevisions: [], brandKits: {}, assetUrls: {}, artRunInputs: {}, artImages: {}, imageQuotes: {},
      // Direct Messages (contract 2.14.0 proposal, studioAutomations.fake.js).
      directMessages: emptyDirectMessages(),
      // The 2xx answers to run actions sent with an `Idempotency-Key`, by key, with their request.
      replays: {},
      // Exports: each one freezes a saved revision (a script's, or one page of an art's).
      exports: [],
      features: { sse: false, audio: false, memory: true, exports: false, mobile_handoff: true, trending_topics: false, social_onboarding: false, payments: false, personality: false,
        art_personal_templates: false, art_image_generation: false, community_images: false } };
  };
  reset();

  const fail = (code, status, details = {}, errors = {}, retryable = false) => { throw Object.assign(new Error(code), { studio: true, code, status, details, errors, retryable }); };
  // The ceiling, or the whole balance when lower, as long as it covers the kind's minimum.
  const reserve = (store, quote, minimum = MINIMUMS[quote.kind] ?? quote.quoted_units) => {
    if (store.credits < Math.min(minimum, quote.quoted_units)) fail('insufficient_credits', 409);
    const units = Math.min(quote.quoted_units, store.credits);
    store.credits -= units;
    return units;
  };
  const creator = () => db.creator || fail('creator_not_initialized', 404);
  const project = projectId => { creator(); return db.projects.find(p => p.id === projectId) || fail('not_found', 404); };
  const chat = chatId => db.projects.find(p => p.chat_id === chatId) || fail('not_found', 404);
  const findRun = runId => db.runs.find(r => r.id === runId) || fail('not_found', 404);
  const touch = item => { item.version += 1; item.timestamps.updated = now(); return item; };
  const message = (p, role, purpose, text, extra = {}) => {
    const item = { id: nextId('stm'), chat_id: p.chat_id, project_id: p.id, role, text, purpose, transcription_id: null, run_id: null, timestamps: stamps(), ...extra };
    db.messages.push(item);
    return item;
  };
  const onboardingOn = () => { if (!db.features.social_onboarding) fail('onboarding_unavailable', 403); };
  // 2.0.0: `creator.profiles` lists every connected profile, none primary; the first comes
  // from the onboarding like the others (`features.multiple_profiles` only for a second one).
  // One @ appears once, and the limit counts every connected profile.
  const connected = owner => owner.profiles || [];
  const sameHandle = (a, b) => a.platform === b.platform && a.handle.toLowerCase() === b.handle.toLowerCase();
  const ownsHandle = (owner, account) => connected(owner).some(item => sameHandle(item, account));
  // The workspace a request names: a connected profile of the creator, else 422 on `profile_id`.
  const workspace = profileId => connected(creator()).find(item => item.id === profileId)
    || fail('validation_error', 422, {}, { profile_id: ['The profile was not found among the connected profiles.'] });
  const profileRow = ({ handle = 'creator.test', locale = 'en_US', ...fields } = {}) => ({ id: nextId('spr'), platform: 'instagram', handle, name: null, picture: null,
    interests: ['fitness'], audience: null, voice_instructions: null, locale, memory_enabled: true, version: 1, ...fields, timestamps: profileStamps() });
  const openOnboarding = expectedVersion => {
    onboardingOn();
    const record = db.onboarding || fail('onboarding_not_started', 404);
    if (record.status !== 'questioning') fail('onboarding_not_open', 409);
    if (expectedVersion !== record.version) fail('version_conflict', 409, { current_version: record.version });
    return record;
  };
  // Uploaded audio of this creator in one scope: a project's, or the home prompt's (null).
  const uploadedAudio = (assetId, projectId) => {
    const asset = db.assets.find(a => a.id === assetId && a.creator_id === creator().id && a.purpose === 'audio' && a.project_id === projectId);
    if (asset?.status !== 'uploaded') fail('validation_error', 422, {}, { asset_id: ['The audio was not found in this scope.'] });
    return asset;
  };
  const transcriptionQuote = (projectId, assetId) => {
    const asset = uploadedAudio(assetId, projectId);
    const quote = { id: nextId('suq'), creator_id: asset.creator_id, project_id: projectId, kind: 'transcribe', source_message_id: null, base_revision_id: null, asset_id: asset.id,
      policy_version: POLICY, unit: 'studio_credit', quoted_units: CEILINGS.transcribe, status: 'available', timestamps: { ...stamps(), expires: later(QUOTE_TTL_MS) }, action_id: null };
    db.quotes.push(quote);
    return quote;
  };
  const openTranscription = (projectId, { asset_id, usage_quote_id, locale }) => {
    const asset = uploadedAudio(asset_id, projectId);
    const quote = db.quotes.find(q => q.id === usage_quote_id && q.asset_id === asset.id && q.status === 'available')
      || fail('validation_error', 422, {}, { usage_quote_id: ['The quote is not available for this audio.'] });
    const units = reserve(db, quote);
    quote.status = 'consumed';
    const transcription = { id: nextId('str'), project_id: projectId, creator_id: asset.creator_id, asset_id: asset.id, usage_operation_id: nextId('suo'),
      status: 'queued', text: null, locale, version: 1, error: null, timestamps: { ...stamps(), started: null, completed: null, reviewed: null } };
    db.transcriptions.push(transcription);
    db.charges[transcription.id] = units;
    return transcription;
  };
  // ── payments ──
  const paymentsOn = () => { if (!db.features.payments) fail('payments_unavailable', 503, {}, {}, true); };
  const currencyFor = requested => {
    const fixed = db.payments.currency;
    if (fixed && requested && requested !== fixed) fail('currency_not_supported', 422, { currency: fixed });
    return fixed || requested || (db.creator?.locale?.startsWith('pt') ? 'brl' : 'usd');
  };
  // Allowance first: the purchased share is what is left once the allowance is spent.
  const balance = () => { const purchased = Math.min(db.payments.purchased, db.credits); return { allowance: db.credits - purchased, purchased }; };
  const renewAllowance = sku => {
    const { allowance, purchased } = balance();
    db.payments.purchased = purchased;
    db.credits = db.credits - allowance + planOf(sku).monthly_credits;
  };
  const restartCycle = (subscription, sku, at = Date.now()) => {
    const start = Math.floor(at / 1000) * 1000; // Stripe timestamps are whole seconds
    Object.assign(subscription, { plan: sku, amount: amountIn(planOf(sku).prices, subscription.currency), pending_change: null,
      current_period_start: new Date(start).toISOString(), current_period_end: new Date(start + MONTH_MS).toISOString() });
    renewAllowance(sku);
  };
  const subscriptionView = () => {
    const subscription = db.payments.subscription;
    if (!subscription) {
      return { id: null, plan: 'free', status: 'none', currency: null, amount: null, interval: null, current_period_start: null, current_period_end: null,
        cancel_at_period_end: false, cancel_at: null, pending_change: null, payment_method: null, allowance: { units: 0, monthly_units: 0, expires_at: null },
        features: { trending_topics: false }, version: 0, timestamps: { created: null, updated: null, canceled: null, ended: null } };
    }
    const plan = planOf(subscription.plan);
    return { ...subscription, allowance: { units: balance().allowance, monthly_units: plan.monthly_credits, expires_at: subscription.current_period_end }, features: plan.features };
  };
  // What `invoice.*` webhooks mirror: one row per Stripe invoice. A subscription invoice
  // charges the plan line's period; a pack invoice has no period and carries its credits.
  const recordInvoice = ({ kind = 'subscription', reason = null, plan = null, units = null, total, currency, period = null, status = 'paid', at = Date.now() }) => {
    if (!db.payments.invoices) db.payments.invoices = [];
    const id = nextId('sin');
    const created = new Date(Math.floor(at / 1000) * 1000).toISOString();
    const paid = status === 'paid';
    const invoice = { id, number: `F3A9C21B-${String(db.payments.invoices.length + 1).padStart(4, '0')}`, kind, billing_reason: reason, plan, units, status, currency,
      subtotal: total, total, amount_paid: paid ? total : 0, amount_due: total, period_start: period?.[0] ?? null, period_end: period?.[1] ?? null,
      hosted_invoice_url: `https://invoice.stripe.com/i/acct_mock/test_${id}`, invoice_pdf: `https://pay.stripe.com/invoice/acct_mock/test_${id}/pdf`,
      timestamps: { created, paid: paid ? created : null } };
    db.payments.invoices.push(invoice);
    return invoice;
  };
  const cycleInvoice = (subscription, reason, total = subscription.amount, status = 'paid') => recordInvoice({ reason, plan: subscription.plan, total, currency: subscription.currency, status,
    period: [subscription.current_period_start, subscription.current_period_end], at: Date.parse(subscription.current_period_start) });
  // `started` backdates the current cycle (a subscriber midway through the month); `since`
  // backdates the subscription itself, billing one renewal per month up to `started`.
  const startSubscription = (sku, currency, { started, since, ...overrides } = {}) => {
    const first = since ?? started ?? Date.now();
    db.payments.subscription = { id: nextId('ssb'), plan: sku, status: 'active', currency, amount: null, interval: 'month', current_period_start: null, current_period_end: null,
      cancel_at_period_end: false, cancel_at: null, pending_change: null, payment_method: { brand: 'visa', last4: '4242' }, version: 1,
      timestamps: { created: new Date(first).toISOString(), updated: now(), canceled: null, ended: null }, ...overrides };
    const subscription = db.payments.subscription;
    db.payments.currency = currency;
    restartCycle(subscription, sku, first);
    cycleInvoice(subscription, 'subscription_create');
    for (let at = first + MONTH_MS; started !== undefined && at <= started; at += MONTH_MS) {
      restartCycle(subscription, sku, at);
      // A renewal Stripe could not charge stays open (`past_due`).
      cycleInvoice(subscription, 'subscription_cycle', subscription.amount, subscription.status === 'past_due' && at + MONTH_MS > started ? 'open' : 'paid');
    }
    return subscription;
  };
  const upgradeable = (subscription, plan) => {
    if (subscription.status !== 'active') fail('subscription_not_active', 409);
    if (!planOf(plan) || planOf(plan).rank <= planOf(subscription.plan).rank || subscription.pending_change) fail('plan_change_not_allowed', 409);
  };
  // What Stripe's invoice preview answers: the new plan's month minus the unused time on
  // the current one, pinned to `proration_date` (second precision).
  const previewUpgrade = (subscription, plan, at = Date.now()) => {
    const start = Date.parse(subscription.current_period_start);
    const end = Date.parse(subscription.current_period_end);
    const unused = Math.min(Math.max((end - at) / (end - start), 0), 1);
    const credit = Math.round((subscription.amount ?? 0) * unused);
    const amount = amountIn(planOf(plan).prices, subscription.currency);
    const pinned = new Date(Math.floor(at / 1000) * 1000);
    return { plan, currency: subscription.currency, amount_due: Math.max(amount - credit, 0), amount, credit, proration_date: pinned.toISOString(),
      renews_at: new Date(pinned.getTime() + MONTH_MS).toISOString(), payment_method: subscription.payment_method };
  };
  // Trending topics: on by default/override (`features.trending_topics`), or by a plan that
  // includes them (Pro) while the subscription is active.
  const topicsOn = () => {
    const subscription = db.payments.subscription;
    return !!db.features.trending_topics || (subscription?.status === 'active' && !!planOf(subscription.plan).features.trending_topics);
  };
  // Core scopes an asset by profile without returning it on StudioAsset.
  const assetView = ({ profile_id, ...asset }) => asset;
  const findCheckout = checkoutId => db.payments.checkouts.find(item => item.id === checkoutId) || fail('checkout_not_found', 404);
  const findTranscription = transcriptionId => db.transcriptions.find(t => t.id === transcriptionId && t.creator_id === creator().id) || fail('not_found', 404);
  const writeRevision = (p, { title, blocks, evidence_ids = [] }, origin, runId = null, suggestions = []) => {
    const full = blocks.map(block => ({ ...block, id: block.id || nextId('sbl') }));
    const base = db.revisions.find(r => r.id === p.latest_revision_id);
    const revision = { id: nextId('srv'), project_id: p.id, creator_id: p.creator_id, parent_revision_id: p.latest_revision_id, run_id: runId, origin,
      revision_number: db.revisions.filter(r => r.project_id === p.id).length + 1,
      script: { title, blocks: full, evidence_ids, spoken_text: full.map(b => b.spoken_text).join('\n\n'), recording_notes: full.map(b => b.recording_notes).filter(Boolean).join('\n\n') },
      suggestions: origin === 'manual' ? clone(base?.suggestions || []) : suggestions,
      source_digest: 'a'.repeat(64), prompt_version: origin === 'ai' ? 'reels-v1.0.0' : null, timestamps: stamps() };
    db.revisions.push(revision);
    p.latest_revision_id = revision.id;
    touch(p);
    return revision;
  };
  // ── Personality ──
  const personalityOn = () => { if (!db.features.personality) fail('personality_unavailable', 403); };
  // A connected profile of this creator; another creator's, or one still in onboarding, is 404.
  const personalityProfile = profileId => { personalityOn(); return connected(creator()).find(item => item.id === profileId) || fail('not_found', 404); };
  const videosOf = profile => db.videos[profile.id] || (db.videos[profile.id] = sampleProfileVideos(profile));
  // A transcript's word count outlives it (core `profile_videos.speech_words`).
  const unavailableReason = video => !video.media_available ? 'copyrighted_media' : video.duration_seconds == null ? 'duration_unknown'
    : db.transcribed[video.id] !== undefined && db.transcribed[video.id] < PERSONALITY.minWords ? 'insufficient_speech'
      : video.duration_seconds < PERSONALITY.min ? 'too_short' : video.duration_seconds > PERSONALITY.max ? 'too_long' : null;
  const activePersonality = profileId => db.personalities[profileId]?.personality || null;
  const videoView = (profile, video) => {
    const reason = unavailableReason(video);
    return { id: video.id, profile_id: profile.id, format: 'instagram::reels', permalink: video.permalink, caption: video.caption,
      preview: { url: video.cover, timestamps: { expires: null } }, duration_seconds: video.duration_seconds,
      metrics: { views: video.views, likes: video.likes, comments: video.comments }, selectable: !reason, unavailable_reason: reason,
      in_personality: !!activePersonality(profile.id)?.sources.some(source => source.video_id === video.id), timestamps: { published: video.published, synced: now() } };
  };
  // Exactly 3 distinct videos of the profile, each still selectable.
  const personalitySelection = (profile, ids) => {
    if (!Array.isArray(ids) || ids.length !== PERSONALITY.videos || new Set(ids).size !== ids.length) {
      fail('validation_error', 422, {}, { video_ids: [`Choose exactly ${PERSONALITY.videos} distinct videos.`] });
    }
    const videos = ids.map(id => videosOf(profile).find(video => video.id === id) || fail('not_found', 404));
    const errors = Object.fromEntries(videos.map((video, index) => [`video_ids.${index}`, unavailableReason(video)]).filter(([, reason]) => reason).map(([key, reason]) => [key, [reason]]));
    if (Object.keys(errors).length) fail('video_not_selectable', 422, {}, errors);
    return videos;
  };
  // Each video once, also when it is both picked and sampled.
  const transcriptionUnits = videos => Math.ceil([...new Map(videos.map(video => [video.id, video])).values()].filter(video => db.transcribed[video.id] === undefined)
    .reduce((total, video) => total + video.duration_seconds / 60, 0) * TRANSCRIPTION_CREDITS_PER_MINUTE);
  // 2.5.0: the comparison by reach, sampled when the quote is made. `db.performance` is the
  // outcome to reach; the ranking still decides which reels (and may leave too few of them).
  const performanceSample = profile => {
    const outcome = db.performance;
    if (!outcome) return null;
    if (outcome === 'unavailable') return { status: outcome, metric: null, ranked: 0, median: null, videos: [] };
    const ranked = videosOf(profile).slice(0, PERFORMANCE.recent).filter(video => video.views != null && Date.now() - Date.parse(video.published) > PERFORMANCE.minAgeMs)
      .sort((a, b) => b.views - a.views).map((video, index) => ({ video, rank: index + 1 }));
    const values = ranked.map(item => item.video.views).sort((a, b) => a - b);
    const half = Math.floor(values.length / 2);
    const median = !values.length ? null : values.length % 2 ? values[half] : Math.round((values[half - 1] + values[half]) / 2);
    const base = { metric: values.length ? 'views' : null, ranked: ranked.length, median };
    if (!SAMPLED.includes(outcome)) return { ...base, status: outcome, videos: [] };
    const transcribable = ranked.filter(({ video }) => video.media_available && video.duration_seconds != null && video.duration_seconds >= PERSONALITY.min
      && video.duration_seconds <= PERSONALITY.max && !(db.transcribed[video.id] < PERSONALITY.minWords));
    const top = transcribable.filter(({ video }) => video.views >= median * PERFORMANCE.ratio).slice(0, PERFORMANCE.top);
    const bottom = transcribable.filter(item => !top.includes(item)).slice(-PERFORMANCE.bottom);
    const middle = transcribable.filter(item => !top.includes(item) && !bottom.includes(item))
      .sort((a, b) => Math.abs(a.video.views - median) - Math.abs(b.video.views - median)).slice(0, PERFORMANCE.middle);
    if (ranked.length < PERFORMANCE.minRanked || top.length < PERFORMANCE.minTier || bottom.length < PERFORMANCE.minTier) return { ...base, status: 'insufficient_videos', videos: [] };
    const videos = [...top.map(item => ({ ...item, tier: 'top' })), ...middle.map(item => ({ ...item, tier: 'middle' })), ...bottom.map(item => ({ ...item, tier: 'bottom' }))]
      .sort((a, b) => a.rank - b.rank);
    return { ...base, status: outcome, videos };
  };
  const sampled = sample => !!sample?.videos.length;
  // The picked videos plus the sample's to transcribe, the analysis and, with a sample, the comparison.
  const personalityUnits = (videos, sample, kind) => transcriptionUnits([...videos, ...(sample?.videos || []).map(item => item.video)])
    + PERSONALITY_ANALYSIS[kind] + (sampled(sample) ? PERFORMANCE[kind] : 0);
  // In the body, an unknown profile is a 422 on `profile_id` (in the path it is a 404).
  const personalityQuote = ({ profile_id, video_ids }) => {
    personalityOn();
    const profile = workspace(profile_id);
    const videos = personalitySelection(profile, video_ids);
    const sample = performanceSample(profile);
    const quote = { id: nextId('suq'), creator_id: creator().id, project_id: null, kind: 'personality', source_message_id: null, base_revision_id: null, asset_id: null,
      policy_version: POLICY, unit: 'studio_credit', quoted_units: personalityUnits(videos, sample, 'ceiling'), status: 'available', timestamps: { ...stamps(), expires: later(QUOTE_TTL_MS) },
      action_id: null };
    db.quotes.push(quote);
    db.personalityQuotes[quote.id] = { profile_id: profile.id, video_ids: [...video_ids].sort(), sample };
    return quote;
  };
  // The cover is copied to the private bucket when the mapping starts (`StudioPicture`).
  const personalitySource = video => ({ video_id: video.id, format: 'instagram::reels', permalink: video.permalink, caption: video.caption ? video.caption.slice(0, 300) : null,
    preview: { url: video.cover, timestamps: { expires: later(MEDIA_TTL_MS) } }, duration_seconds: video.duration_seconds, words: null, status: 'pending', error_code: null,
    timestamps: { published: video.published } });
  const findMapping = mappingId => db.personalityMappings.find(item => item.id === mappingId && item.creator_id === creator().id) || fail('not_found', 404);
  const latestMapping = profileId => db.personalityMappings.filter(item => item.profile_id === profileId).slice(-1)[0] || null;
  const personalityError = (code, message, retryable, errors = {}) => ({ message, code, request_id: 'sreq-0000003', retryable, errors, details: {}, timestamps: { occurred: now() } });
  const art = createArtRoutes({ db: () => db, fail, nextId, creator, project, workspace, touch, now, stamps, clone, message, settle, reserve });
  const dm = createAutomationRoutes({ db: () => db, fail, nextId, now, clone, profiles: () => db.creator?.profiles || [], plan: () => db.payments.subscription?.plan || 'free',
    assetUrl: id => db.assetUrls[id] || `https://uploads.studio.test/${id}` });
  // ── Exports ── A script export freezes a saved script revision (PDF/DOCX); an art export
  // (2.7.0, studioArt.fake.js) one page of an art version (PNG/JPEG). The browser renders the
  // file, uploads it (`purpose: export`) and completes the export, or fails it.
  const freezeScript = (p, { revision_id: revisionId, format } = {}) => {
    const revision = db.revisions.find(item => item.id === revisionId && item.project_id === p.id);
    const errors = {};
    if (!revision) errors.revision_id = ['Not a saved revision of this project.'];
    if (!['pdf', 'docx'].includes(format)) errors.format = ['pdf or docx.'];
    if (Object.keys(errors).length) fail('validation_error', 422, {}, errors);
    return { revision_id: revision.id, art_revision_id: null, format, scale: null, page_id: null, renderer_version: 'studio-web-v1', document: clone(revision.script), source_digest: revision.source_digest };
  };
  const findExport = exportId => db.exports.find(item => item.id === exportId && item.creator_id === creator().id) || fail('not_found', 404);
  // ── Run actions ── The answer to the run's open action, at the version read.
  const answerAction = (runId, actionId, body) => {
    const run = findRun(runId);
    const action = run.pending_actions.find(a => a.id === actionId && a.status === 'open') || fail('action_not_open', 409);
    if (body.expected_action_version !== action.version) fail('action_version_conflict', 409);
    if (!action.allowed_responses.includes(body.kind)) fail('validation_error', 422);
    // 2.9.0: an art run's image question (`choose_images` or `skip`) goes back to its images.
    if (action.kind === 'images') art.respondImages(run, action, body);
    else {
      if (body.kind === 'submit_story' && !db.messages.some(m => m.id === body.message_id && m.purpose === 'story')) fail('validation_error', 422);
      run.pending_actions = []; run.status = 'running'; run.phase = 'writing';
    }
    touch(run);
    return { id: nextId('sar'), action_id: action.id, run_id: run.id, kind: body.kind, run: art.runView(run), timestamps: stamps() };
  };
  const exportFileName = record => {
    const title = db.projects.find(item => item.id === record.project_id)?.title;
    const number = record.page_id && record.document.pages.length > 1 ? `-${record.document.pages.findIndex(item => item.id === record.page_id) + 1}` : '';
    return `${slug(title)}${number}.${EXPORT_FILES[record.format][1]}`;
  };

  const handlers = {
    ...art.handlers,
    ...dm.routes,
    capabilities: () => ({ contract_version: db.contract, available: db.available, formats: ['instagram::reels', ...db.artFormats], features: { ...db.features, trending_topics: topicsOn() },
      onboarding_platforms: db.features.social_onboarding ? db.onboardingPlatforms : [],
      limits: { prompt_characters: 20000, audio_bytes: 26214400, audio_duration_seconds: 600, audio_mime_types: ['audio/webm'], event_retention_seconds: 604800, poll_interval_seconds: 3, handoff_ttl_seconds: null, mobile_session_ttl_seconds: null, download_ttl_seconds: 900, max_active_runs: 1, max_profiles: db.maxProfiles,
        personality_videos: PERSONALITY.videos, personality_video_min_seconds: PERSONALITY.min, personality_video_max_seconds: PERSONALITY.max },
      usage_policy: { version: '2026-10-v1', unit: 'studio_credit', settlement: 'successful_operation_only', quote_required: true }, timestamps: { observed: now() } }),
    creator: () => creator(),
    // 2.0.0: the account alone; its profiles come from the @ onboarding.
    createCreator: ({ locale }) => {
      onboardingOn();
      if (db.creator) fail('creator_already_exists', 409);
      db.creator = { id: nextId('scp'), locale, onboarding_status: 'pending', profiles: [], version: 1, timestamps: stamps() };
      return db.creator;
    },
    updateCreator: ({ expected_version, locale }) => {
      const current = creator();
      if (expected_version !== current.version) fail('version_conflict', 409, { current_version: current.version });
      current.locale = locale;
      return touch(current);
    },
    // 2.0.0: one profile's workspace settings, partial and versioned.
    updateProfile: (profileId, { expected_version, ...body }) => {
      const profile = connected(creator()).find(item => item.id === profileId) || fail('not_found', 404);
      if (expected_version !== profile.version) fail('version_conflict', 409, { current_version: profile.version });
      Object.assign(profile, body);
      return touch(profile);
    },
    onboarding: () => { onboardingOn(); return db.onboarding || fail('onboarding_not_started', 404); },
    // Maps an @ into a profile — lookup now, analysis in the queue, connected on completion.
    startOnboarding: ({ account, locale }) => {
      onboardingOn();
      const owner = creator();
      const another = connected(owner).length > 0;
      if (another && !db.features.multiple_profiles) fail('onboarding_already_completed', 409);
      if (!account) fail('validation_error', 422, {}, { account: ['The account is required.'] });
      if (!db.onboardingPlatforms.includes(account.platform)) fail('validation_error', 422, {}, { 'account.platform': ['The platform is not available for onboarding.'] });
      if (!HANDLE.test(account.handle || '')) fail('validation_error', 422, {}, { 'account.handle': ['The handle is invalid.'] });
      // Before the lookup, as in core: a known @ or a full creator never spends platform quota.
      if (ownsHandle(owner, account)) fail('profile_already_connected', 409);
      if (connected(owner).length >= db.maxProfiles) fail('profile_limit_reached', 409);
      if (db.lookup === 'not_found') fail('social_account_not_found', 422, {}, { 'account.handle': ['No professional account was found for this handle.'] });
      if (db.lookup === 'unavailable') fail('platform_unavailable', 503, {}, {}, true);
      if (db.lookup === 'rate_limited') fail('rate_limited', 429, { retry_after_seconds: 60 }, {}, true);
      // A new start replaces the open onboarding and the profile it opened.
      db.onboarding = { id: nextId('sob'), status: 'analyzing', account: sampleAccount(account.platform, account.handle),
        findings: null, questions: [], answers: [], locale, creator_id: owner.id, profile_id: nextId('spr'),
        version: 1, timestamps: { ...stamps(), analyzed: null, completed: null } };
      return db.onboarding;
    },
    answerOnboarding: ({ expected_version, ...body }) => {
      const record = openOnboarding(expected_version);
      const { answer, error } = buildAnswer(record, body);
      if (error) fail(error.code, error.status, error.details, error.errors);
      record.answers = [...record.answers.filter(a => a.question_id !== answer.question_id), answer];
      return touch(record);
    },
    completeOnboarding: ({ intent, expected_version, memory_enabled }) => {
      if (intent !== 'complete' || typeof memory_enabled !== 'boolean') fail('validation_error', 422);
      const record = openOnboarding(expected_version);
      const missing = missingRequired(record);
      if (missing.length) fail('onboarding_incomplete', 409, { question_ids: missing.map(q => q.id) });
      // The answers connect the profile the onboarding opened; the account keeps none of them.
      const { interests, audience, voice_instructions } = onboardingProfile(record, memory_enabled);
      const owner = creator();
      const { platform, handle, name, picture } = record.account;
      const first = connected(owner).length === 0;
      owner.profiles = [...connected(owner), { id: record.profile_id, platform, handle, name: name || null, picture: picture || null, interests, audience, voice_instructions,
        locale: record.locale, memory_enabled, version: 2, timestamps: profileStamps(record.timestamps.created) }];
      owner.onboarding_status = 'completed';
      // Rows written before the account had a profile join the first one.
      if (first) [db.projects, db.memories, db.assets].forEach(rows => rows.filter(row => !row.profile_id).forEach(row => { row.profile_id = record.profile_id; }));
      record.status = 'completed'; record.timestamps.completed = now();
      return touch(record);
    },
    usage: () => {
      const { allowance, purchased } = balance();
      return { creator_id: creator().id, unit: 'studio_credit', available_units: db.credits, reserved_units: 0,
        allowance_units: allowance, allowance_expires_at: db.payments.subscription?.current_period_end ?? null, purchased_units: purchased,
        policy_version: POLICY, timestamps: { observed: now() } };
    },
    // The snapshot is shared; core ranks it per profile (`profile_version`). The double keeps one order.
    topics: (params = {}) => {
      const profile = workspace(params.profile_id);
      if (!topicsOn()) fail('trending_topics_unavailable', 403);
      const snapshot = topicSnapshot(db.topics);
      return { ...page(db.topics), profile_version: profile.version, source_state: snapshot.state, limitations: [], timestamps: { observed: now() }, snapshot };
    },
    topic: (topicId, params = {}) => { workspace(params.profile_id); if (!topicsOn()) fail('trending_topics_unavailable', 403); return db.topics.find(item => item.id === topicId) || fail('not_found', 404); },
    projects: (params = {}) => { const profile = workspace(params.profile_id); return page(db.projects.filter(p => p.status === 'active' && p.profile_id === profile.id)); },
    project: projectId => project(projectId),
    createProject: body => {
      const owner = creator();
      const profile = workspace(body.profile_id);
      // 1.5.0: the reviewed transcription of a prompt recorded without a project is adopted, with its audio —
      // only by a project of the profile it was recorded in (2.0.0).
      const voice = body.transcription_id ? db.transcriptions.find(t => t.id === body.transcription_id && t.creator_id === owner.id && t.project_id === null
        && db.assets.find(a => a.id === t.asset_id)?.profile_id === profile.id)
        || fail('validation_error', 422, {}, { transcription_id: ['The transcription was not found or is already used.'] }) : null;
      if (voice && voice.status !== 'reviewed') fail('transcription_not_reviewed', 409);
      if (art.server.isArt({ brief: body.brief })) art.checkBrief(body.brief, profile);
      const attached = art.checkAttachments(body.attachments, { profile, art: art.server.isArt({ brief: body.brief }) });
      const id = nextId('spj');
      const p = { id, creator_id: owner.id, profile_id: profile.id, chat_id: id.replace('spj-', 'stc-'), initial_message_id: null, title: body.title || body.prompt.split('\n')[0].slice(0, 60),
        status: 'active', brief: body.brief, version: 1, active_run_id: null, latest_revision_id: null, approved_revision_id: null, approval_id: null, timestamps: stamps() };
      p.initial_message_id = message(p, 'user', 'instruction', body.prompt, { transcription_id: voice?.id || null, ...(attached.length ? { attachments: attached } : {}) }).id;
      if (voice) {
        voice.project_id = id;
        db.assets.find(a => a.id === voice.asset_id).project_id = id;
        db.quotes.filter(q => q.asset_id === voice.asset_id).forEach(q => { q.project_id = id; });
      }
      db.projects.unshift(p);
      return p;
    },
    updateProject: (projectId, { expected_version, ...body }) => {
      const p = project(projectId);
      if (expected_version !== p.version) fail('version_conflict', 409, { current_version: p.version });
      Object.assign(p, body);
      return touch(p);
    },
    messages: chatId => { const list = page(db.messages.filter(m => m.chat_id === chat(chatId).chat_id), 100); return { ...list, data: list.data.map(art.messageView) }; },
    createMessage: (chatId, body) => {
      const p = chat(chatId);
      if (body.transcription_id && !db.transcriptions.some(t => t.id === body.transcription_id && t.project_id === p.id && t.status === 'reviewed')) {
        fail('validation_error', 422, {}, { transcription_id: ['The transcription was not found in this project.'] });
      }
      // 2.7.0: an art project's message may carry images (art.checkAttachments).
      const attached = art.checkAttachments(body.attachments, { profile: (db.creator.profiles || []).find(item => item.id === p.profile_id), art: art.server.isArt(p) });
      return art.messageView(message(p, 'user', body.purpose, body.text, { transcription_id: body.transcription_id || null, ...(attached.length ? { attachments: attached } : {}) }));
    },
    quote: (projectId, body) => {
      const p = project(projectId);
      const id = nextId('suq');
      // 2.9.0: an `image` quote prices the places to generate of the run's open image question.
      const imageUnits = body.kind === 'image' ? art.imageQuote(p, body, id) : null;
      const quote = { id, creator_id: p.creator_id, project_id: p.id, kind: body.kind, source_message_id: body.source_message_id || null,
        base_revision_id: body.base_revision_id || null, asset_id: body.asset_id || null, policy_version: POLICY, unit: 'studio_credit',
        quoted_units: imageUnits ?? CEILINGS[body.kind] ?? 0, status: 'available', timestamps: { ...stamps(), expires: later(QUOTE_TTL_MS) },
        action_id: imageUnits === null ? null : body.action_id };
      db.quotes.push(quote);
      return quote;
    },
    // `POST /usage-quotes`, the creator scope: a prompt recording (`transcribe`) or, since
    // 2.3.0, a profile's personality (`personality`).
    audioQuote: body => {
      if (body.kind === 'personality') return personalityQuote(body);
      if (body.kind !== 'transcribe') fail('validation_error', 422, {}, { kind: ['Only transcribe and personality exist without a project.'] });
      return transcriptionQuote(null, body.asset_id);
    },
    personalityQuote: body => handlers.audioQuote(body),
    // 18 — Personality. The listing reads the profile's videos newest first; the cursor is
    // opaque and bound to the profile (another profile's is a 422 on `cursor`).
    profileVideos: (profileId, { cursor = null, limit = 24 } = {}) => {
      const profile = personalityProfile(profileId);
      if (!Number.isInteger(limit) || limit < 1 || limit > 50) fail('validation_error', 422, {}, { limit: ['The limit must be between 1 and 50.'] });
      const bound = cursor ? /^svc:([^:]+):(\d+)$/.exec(cursor) : null;
      const start = !cursor ? 0 : bound?.[1] === profile.id ? Number(bound[2]) : NaN;
      if (Number.isNaN(start)) fail('validation_error', 422, {}, { cursor: ['The cursor is invalid.'] });
      const all = videosOf(profile);
      const more = start + limit < all.length;
      return { data: all.slice(start, start + limit).map(video => videoView(profile, video)), next_cursor: more ? `svc:${profile.id}:${start + limit}` : null, has_more: more,
        timestamps: { observed: now() } };
    },
    profilePersonality: profileId => {
      const profile = personalityProfile(profileId);
      const mapping = latestMapping(profile.id);
      const personality = activePersonality(profile.id);
      return { profile_id: profile.id, status: mapping && MAPPING_RUNNING.includes(mapping.status) ? 'mapping' : personality ? 'ready' : 'not_configured',
        personality, mapping, timestamps: { updated: db.personalities[profile.id]?.updated || null } };
    },
    startPersonalityMapping: (profileId, { video_ids, usage_quote_id } = {}) => {
      const profile = personalityProfile(profileId);
      if (db.personalityMappings.some(item => item.profile_id === profile.id && MAPPING_RUNNING.includes(item.status))) fail('personality_mapping_in_progress', 409);
      const videos = personalitySelection(profile, video_ids);
      const quote = db.quotes.find(item => item.id === usage_quote_id && item.kind === 'personality' && item.creator_id === creator().id)
        || fail('validation_error', 422, {}, { usage_quote_id: ['The quote was not found.'] });
      if (quote.status === 'consumed') fail('usage_quote_consumed', 409);
      if (quote.status === 'expired' || Date.parse(quote.timestamps.expires) < Date.now()) { quote.status = 'expired'; fail('usage_quote_expired', 410); }
      const target = db.personalityQuotes[quote.id];
      if (target.profile_id !== profile.id || target.video_ids.join() !== [...video_ids].sort().join()) fail('usage_quote_mismatch', 409);
      const units = reserve(db, quote, personalityUnits(videos, target.sample, 'minimum'));
      quote.status = 'consumed';
      const mapping = { id: nextId('spm'), creator_id: creator().id, profile_id: profile.id, status: 'queued', stage: null, sources: videos.map(personalitySource),
        usage_operation_id: nextId('suo'), locale: profile.locale || 'pt_BR', error: null, version: 1,
        timestamps: { created: now(), updated: now(), started: null, completed: null, failed: null } };
      db.personalityMappings.push(mapping);
      db.charges[mapping.id] = units;
      // The mapping runs exactly the sample its quote priced.
      db.performanceSamples[mapping.id] = target.sample ?? null;
      return mapping;
    },
    personalityMapping: mappingId => findMapping(mappingId),
    // `putUpload` stands in for the presigned PUT: only the asset row matters here.
    prepareAsset: body => {
      const owner = creator();
      // An art image belongs to its profile's gallery (2.7.0 proposal); a project's file follows
      // the project; prompt audio names its workspace (2.0.0).
      const artImage = body.purpose === 'art_image';
      if (artImage && (!ART_IMAGE_MIME_TYPES.includes(body.mime_type) || !(body.byte_size > 0 && body.byte_size <= ART_IMAGE_MAX_BYTES) || !(body.image?.width > 0 && body.image?.height > 0))) {
        fail('validation_error', 422, {}, { mime_type: ['PNG, JPEG or WebP up to 15 MB, with its pixel size.'] });
      }
      // 2.13.0: a video of the profile's, for carousels (403 while no carousel is offered).
      const artVideo = body.purpose === 'art_video';
      if (artVideo && !db.artFormats.includes('instagram::carousel')) fail('studio_unavailable', 403);
      if (artVideo && (!ART_VIDEO_MIME_TYPES.includes(body.mime_type) || !(body.byte_size > 0 && body.byte_size <= ART_VIDEO_MAX_BYTES)
        || !(body.video?.width > 0 && body.video?.height > 0 && body.video?.duration >= 0.1 && body.video?.duration <= 900))) {
        fail('validation_error', 422, {}, { mime_type: ['MP4, MOV or WebM up to 300 MB, with its pixel size and length (0.1 to 900 s).'] });
      }
      const exportVideo = body.purpose === 'export' && body.mime_type === 'video/mp4';
      if (exportVideo && body.byte_size > 100 * 1024 * 1024) fail('validation_error', 422, {}, { byte_size: ['An MP4 export is up to 100 MB.'] });
      const profileId = artImage || artVideo ? workspace(body.profile_id).id : body.project_id || body.purpose !== 'audio' ? project(body.project_id).profile_id : workspace(body.profile_id).id;
      // 2.9.0: an art image is an `upload`, undescribed until the creator (or core's vision model) describes it.
      const asset = { id: nextId('sas'), creator_id: owner.id, profile_id: profileId, project_id: body.project_id || null, export_id: body.export_id || null, purpose: body.purpose,
        status: 'awaiting_upload', mime_type: body.mime_type, byte_size: body.byte_size, sha256: null, download: null,
        ...(artImage ? { image: { width: body.image.width, height: body.image.height }, origin: 'upload', description: null } : {}),
        ...(artVideo ? { video: { width: body.video.width, height: body.video.height, duration: body.video.duration }, origin: 'upload', description: null } : {}),
        timestamps: { ...stamps(), uploaded: null, upload_expires: later(UPLOAD_TTL_MS), download_expires: null } };
      asset.upload = { url: `https://uploads.studio.test/${asset.id}`, method: 'PUT', headers: {} };
      db.assets.push(asset);
      return assetView(asset);
    },
    putUpload: () => null,
    // `PATCH /assets/{id}` confirms an upload, or (2.9.0) describes an art image (`describeAsset`).
    confirmAsset: (assetId, body = {}) => {
      if (body.description !== undefined) return art.handlers.describeAsset(assetId, body);
      const { status, sha256 } = body;
      const asset = db.assets.find(a => a.id === assetId && a.creator_id === creator().id) || fail('not_found', 404);
      if (status !== 'uploaded' || !/^[0-9a-f]{64}$/.test(sha256 || '')) fail('validation_error', 422);
      Object.assign(asset, { status: 'uploaded', sha256, upload: null });
      Object.assign(asset.timestamps, { updated: now(), uploaded: now(), upload_expires: null });
      // An art image is shown right away: its confirmation carries the signed GET.
      if (asset.purpose === 'art_image' || asset.purpose === 'art_video') return { ...assetView(asset), download: { url: db.assetUrls[asset.id] || `https://uploads.studio.test/${asset.id}` } };
      return assetView(asset);
    },
    startTranscription: (projectId, body) => openTranscription(project(projectId).id, body),
    startAudioTranscription: body => openTranscription(null, body),
    transcription: transcriptionId => findTranscription(transcriptionId),
    updateTranscription: (transcriptionId, { intent, expected_version, text }) => {
      const transcription = findTranscription(transcriptionId);
      if (intent !== 'review' || !text?.trim()) fail('validation_error', 422);
      if (expected_version !== transcription.version) fail('version_conflict', 409, { current_version: transcription.version });
      if (!['ready', 'reviewed'].includes(transcription.status)) fail('invalid_transcription_transition', 409);
      Object.assign(transcription, { status: 'reviewed', text });
      transcription.timestamps.reviewed = now();
      return touch(transcription);
    },
    startRun: (projectId, body) => {
      const p = project(projectId);
      if (body.expected_project_version !== p.version) fail('version_conflict', 409, { current_version: p.version });
      if (db.runs.some(r => r.project_id === p.id && ACTIVE.includes(r.status))) fail('active_run_exists', 409);
      const artInputs = art.checkRun(p, body);
      const quote = db.quotes.find(q => q.id === body.usage_quote_id && q.status === 'available') || fail('validation_error', 422);
      const units = reserve(db, quote);
      quote.status = 'consumed';
      const run = { id: nextId('srn'), project_id: p.id, chat_id: p.chat_id, creator_id: p.creator_id, usage_operation_id: nextId('suo'), source_message_id: body.source_message_id,
        base_revision_id: body.base_revision_id || null, result_revision_id: null, outcome: null, kind: body.kind, status: 'queued', phase: 'brief', version: 1, attempt: 1,
        prompt_version: artInputs ? 'art-v1.0.0' : 'reels-v1.2.0', event_cursor: null, pending_actions: [], error: null,
        timestamps: { ...stamps(), started: null, waiting: null, completed: null, cancelled: null } };
      db.runs.push(run);
      db.charges[run.id] = units;
      if (artInputs) db.artRunInputs[run.id] = artInputs;
      p.active_run_id = run.id;
      return run;
    },
    // A run as read signs its open image question's URLs and draft (art.runView).
    run: runId => art.runView(findRun(runId)),
    updateRun: (runId, { intent, expected_version }) => {
      const run = findRun(runId);
      if (expected_version !== run.version) fail('version_conflict', 409, { current_version: run.version });
      const p = db.projects.find(item => item.id === run.project_id);
      if (intent === 'cancel') {
        if (!ACTIVE.includes(run.status)) fail('invalid_run_transition', 409);
        run.status = 'cancelled'; run.pending_actions = []; run.timestamps.cancelled = now(); p.active_run_id = null;
        art.server.releaseImages(run.id);
      } else {
        if (run.status !== 'failed' || !run.error?.retryable) fail('invalid_run_transition', 409);
        run.status = 'queued'; run.error = null; run.attempt += 1; p.active_run_id = run.id;
      }
      return art.runView(touch(run));
    },
    // `Idempotency-Key` as core's IdempotentRequest keeps it: the same key and request replay
    // the 2xx answer already given, the same key with another request is a 409.
    respond: (runId, actionId, body, key) => {
      const request = JSON.stringify([runId, actionId, body]);
      const kept = key ? db.replays[key] : null;
      if (kept) { if (kept.request !== request) fail('idempotency_key_reused', 409); return clone(kept.response); }
      const response = answerAction(runId, actionId, body);
      if (key) db.replays[key] = { request, response: clone(response) };
      return response;
    },
    revisions: projectId => page(db.revisions.filter(r => r.project_id === project(projectId).id).slice().reverse()),
    revision: revisionId => db.revisions.find(r => r.id === revisionId) || fail('not_found', 404),
    saveRevision: (projectId, body) => {
      const p = project(projectId);
      if (body.base_revision_id !== p.latest_revision_id || body.expected_project_version !== p.version) {
        fail('revision_conflict', 409, { current_version: p.version, current_revision_id: p.latest_revision_id });
      }
      return writeRevision(p, body, 'manual');
    },
    // 08 — Exports. No credits; `awaiting_upload` until the browser delivers the file.
    createExport: (projectId, body = {}) => {
      const p = project(projectId);
      const frozen = body.art_revision_id !== undefined || art.server.isArt(p) ? art.freezeExport(p, body) : freezeScript(p, body);
      const record = { id: nextId('sxp'), project_id: p.id, creator_id: p.creator_id, approval_id: null, ...frozen, status: 'awaiting_upload', asset_id: null, download: null, error: null,
        timestamps: { ...stamps(), completed: null, download_expires: null } };
      db.exports.push(record);
      return art.exportView(record);
    },
    export: exportId => art.exportView(findExport(exportId)),
    // `complete` takes the uploaded file of this export, in its format, rendered from the
    // frozen document (`source_digest`) by its renderer; `fail` records the browser's failure;
    // `retry` reopens a failed or expired export on the same frozen document.
    updateExport: (exportId, body = {}) => {
      const record = findExport(exportId);
      if (body.intent === 'complete') {
        if (record.status !== 'awaiting_upload') fail('invalid_export_transition', 409);
        const [mime] = EXPORT_FILES[record.format];
        const asset = db.assets.find(item => item.id === body.asset_id && item.creator_id === record.creator_id && item.purpose === 'export' && item.status === 'uploaded'
          && (!item.export_id || item.export_id === record.id));
        const errors = {};
        if (!asset || asset.mime_type !== mime) errors.asset_id = [`An uploaded ${mime} file of this export.`];
        if (body.source_digest !== record.source_digest) errors.source_digest = ['Does not match the frozen document.'];
        if (body.renderer_version !== record.renderer_version) errors.renderer_version = [`Must be ${record.renderer_version}.`];
        if (Object.keys(errors).length) fail('validation_error', 422, {}, errors);
        Object.assign(record, { status: 'ready', asset_id: asset.id, error: null, download: { url: db.assetUrls[asset.id] || `https://uploads.studio.test/${asset.id}`, file_name: exportFileName(record), mime_type: mime } });
        Object.assign(record.timestamps, { completed: now(), download_expires: later(DOWNLOAD_TTL_MS) });
      } else if (body.intent === 'fail') {
        if (record.status !== 'awaiting_upload') fail('invalid_export_transition', 409);
        if (!['render_failed', 'upload_failed'].includes(body.failure_code)) fail('validation_error', 422, {}, { failure_code: ['render_failed or upload_failed.'] });
        Object.assign(record, { status: 'failed', error: { message: 'The file could not be delivered.', code: body.failure_code, request_id: nextId('sreq'), retryable: true, errors: {}, details: {}, timestamps: { occurred: now() } } });
      } else if (body.intent === 'retry') {
        if (!['failed', 'expired'].includes(record.status)) fail('invalid_export_transition', 409);
        Object.assign(record, { status: 'awaiting_upload', asset_id: null, download: null, error: null });
        Object.assign(record.timestamps, { completed: null, download_expires: null });
      } else fail('validation_error', 422, {}, { intent: ['complete, fail or retry.'] });
      record.timestamps.updated = now();
      return art.exportView(record);
    },
    memories: (params = {}) => { const profile = workspace(params.profile_id); return page(db.memories.filter(m => m.profile_id === profile.id)); },
    createMemory: body => {
      const owner = creator();
      const profile = workspace(body.profile_id);
      if (!profile.memory_enabled) fail('memory_disabled', 409);
      const existing = db.memories.find(m => m.text === body.text && m.profile_id === profile.id);
      if (existing) return existing;
      const memory = { id: nextId('smm'), creator_id: owner.id, profile_id: profile.id, text: body.text, status: body.sensitive ? 'proposed' : 'confirmed', sensitive: body.sensitive, version: 1,
        provenance: { kind: 'explicit', message_id: null, transcription_id: null }, timestamps: { ...stamps(), confirmed: now() } };
      db.memories.unshift(memory);
      return memory;
    },
    updateMemory: (memoryId, { expected_version, ...body }) => {
      const memory = db.memories.find(m => m.id === memoryId) || fail('not_found', 404);
      if (expected_version !== memory.version) fail('memory_version_conflict', 409);
      Object.assign(memory, body);
      return touch(memory);
    },
    deleteMemory: memoryId => { db.memories = db.memories.filter(m => m.id !== memoryId); return null; },

    // 11 — Plans & payments. Reads work with payments off; the catalog and mutations do not.
    plans: () => {
      paymentsOn();
      return { publishable_key: 'pk_test_mock', currencies: ['brl', 'usd', 'eur'], default_currency: currencyFor(null), currency_locked: !!db.payments.currency,
        plans: PLANS, credit_pack: CREDIT_PACK, timestamps: { observed: now() } };
    },
    subscription: () => subscriptionView(),
    createCheckout: ({ kind, plan, quantity, currency }) => {
      paymentsOn();
      creator();
      if (kind === 'subscription') {
        if (!['standard', 'pro'].includes(plan)) fail('validation_error', 422, {}, { plan: ['The plan is invalid.'] });
        if (db.payments.subscription) fail('subscription_exists', 409);
      } else if (kind === 'credits') {
        if (!Number.isInteger(quantity) || quantity < CREDIT_PACK.min_quantity || quantity > CREDIT_PACK.max_quantity) fail('validation_error', 422, {}, { quantity: ['The quantity is out of bounds.'] });
      } else fail('validation_error', 422, {}, { kind: ['The kind is invalid.'] });
      const chosen = currencyFor(currency);
      const credits = kind === 'credits';
      const unit = amountIn(credits ? CREDIT_PACK.prices : planOf(plan).prices, chosen);
      const id = nextId('sck');
      const checkout = { id, kind, origin: 'app', status: 'open', fulfillment: 'pending', plan: credits ? null : plan, quantity: credits ? quantity : null,
        units: credits ? quantity * CREDIT_PACK.credits_per_pack : planOf(plan).monthly_credits, units_granted: 0, currency: chosen,
        amount_total: unit * (credits ? quantity : 1), subscription_id: null, client_secret: `cs_test_${id}_secret_mock`,
        expires_at: new Date(Date.now() + 864e5).toISOString(), timestamps: { created: now(), updated: now(), fulfilled: null } };
      db.payments.checkouts.push(checkout);
      return checkout;
    },
    checkout: checkoutId => findCheckout(checkoutId),
    subscriptionPreview: plan => {
      paymentsOn();
      if (!['standard', 'pro'].includes(plan)) fail('validation_error', 422, {}, { plan: ['The plan is invalid.'] });
      const subscription = db.payments.subscription || fail('subscription_not_found', 404);
      upgradeable(subscription, plan);
      return previewUpgrade(subscription, plan);
    },
    // Upgrade only; the card on file pays now, or the change waits as `pending_change`.
    // `proration_date` pins the previewed amount: at most 30 min old, never ahead (60 s).
    updateSubscription: ({ plan, cancel_at_period_end, proration_date }) => {
      paymentsOn();
      const subscription = db.payments.subscription || fail('subscription_not_found', 404);
      if (typeof cancel_at_period_end === 'boolean') {
        Object.assign(subscription, { cancel_at_period_end, cancel_at: cancel_at_period_end ? subscription.current_period_end : null });
      } else {
        upgradeable(subscription, plan);
        if (proration_date !== undefined) {
          const pinned = Date.parse(proration_date);
          if (Number.isNaN(pinned) || pinned > Date.now() + 60e3 || pinned < Date.now() - 30 * 60e3 || pinned < Date.parse(subscription.current_period_start)) {
            fail('validation_error', 422, {}, { proration_date: ['The proration date is out of range.'] });
          }
        }
        // Unused time on the current plan comes off the new month (`subscription_update`).
        const due = previewUpgrade(subscription, plan).amount_due;
        if (db.payments.upgrade === 'paid') {
          restartCycle(subscription, plan);
          cycleInvoice(subscription, 'subscription_update', due);
        } else {
          const held = recordInvoice({ reason: 'subscription_update', plan, total: due, currency: subscription.currency, status: 'open' });
          subscription.pending_change = { plan, status: db.payments.upgrade, payment_url: held.hosted_invoice_url };
        }
      }
      subscription.version += 1; subscription.timestamps.updated = now();
      return subscriptionView();
    },
    // Newest first, `page`/`per_page` like every Studio list.
    invoices: ({ page: number = 1, per_page: perPage = 15 } = {}) => {
      const list = [...(db.payments.invoices || [])].reverse().sort((a, b) => Date.parse(b.timestamps.created) - Date.parse(a.timestamps.created));
      const last = Math.max(1, Math.ceil(list.length / perPage));
      return { current_page: number, per_page: perPage, total: list.length, last_page: last, data: list.slice((number - 1) * perPage, number * perPage) };
    },
    createPortalSession: () => { paymentsOn(); creator(); return { url: 'https://billing.stripe.com/p/session/test_mock' }; },
  };

  // Every route answers asynchronously with a copy, like the network would.
  const api = Object.fromEntries(Object.entries(handlers).map(([name, fn]) => [name, (...args) => {
    calls.push({ name, args: clone(args) });
    if (db.offline) return Promise.reject(Object.assign(new Error('network'), { studio: true, code: 'network', status: null, details: {}, errors: {} }));
    try { return Promise.resolve(clone(fn(...args))); } catch (error) { return Promise.reject(error); }
  }]));

  // Server-side controls: seed data and play the worker's part, outside the call log.
  api.server = {
    ...art.server,
    ...dm.server,
    reset,
    db: () => db,
    calls: name => calls.filter(call => call.name === name).map(call => call.args),
    // Snapshot and restore the whole store (the browser mock keeps it per tab).
    state: () => clone({ db, sequence }),
    restore: saved => { db = { ...db, ...saved.db, features: { ...db.features, ...saved.db.features } }; sequence = saved.sequence; },
    // The onboarding worker: findings and tailored questions, or generic ones when the analysis is unavailable.
    analyze: (withFindings = db.analysis !== 'none') => {
      const record = db.onboarding;
      if (!record || record.status !== 'analyzing') return clone(record);
      record.findings = withFindings ? sampleFindings(record.locale) : null;
      record.questions = sampleQuestions(record.locale, record.findings);
      record.status = 'questioning'; record.timestamps.analyzed = now();
      return clone(touch(record));
    },
    // The account with its first connected profile (2.0.0), which takes the profile fields
    // (`handle`, `interests`, `audience`, `voice_instructions`, `memory_enabled`, `name`);
    // `extraProfiles` adds more connected ones. `profiles: []` seeds a pending account.
    seedCreator: ({ locale = 'en_US', profiles, extraProfiles = [], ...fields } = {}) => {
      const own = profiles ?? [profileRow({ locale, ...fields }), ...extraProfiles];
      db.creator = { id: nextId('scp'), locale, onboarding_status: own.length ? 'completed' : 'pending', profiles: own, version: 1, timestamps: stamps() };
      return clone(db.creator);
    },
    profileRow: fields => profileRow(fields),
    // A project with one saved manual revision whose single block is `sbl-test001`, in a profile (the first by default).
    seedProject: (prompt = 'My reel', spoken = 'Original', profileId = connected(creator())[0]?.id) => {
      const p = handlers.createProject({ prompt, profile_id: profileId, brief: { format: 'instagram::reels', topic: null, instructions: null } });
      const revision = writeRevision(p, { title: prompt, blocks: [{ id: 'sbl-test001', kind: 'opening', spoken_text: spoken, recording_notes: '' }] }, 'manual');
      return clone({ project: p, revision });
    },
    otherSessionSaves: (projectId, spoken) => {
      const p = db.projects.find(item => item.id === projectId);
      const latest = db.revisions.find(r => r.id === p.latest_revision_id);
      return clone(writeRevision(p, { title: latest.script.title, blocks: latest.script.blocks.map(b => ({ ...b, spoken_text: spoken })) }, 'manual'));
    },
    askStory: runId => {
      const run = db.runs.find(item => item.id === runId);
      run.status = 'waiting_user'; run.phase = 'story';
      run.pending_actions = [{ id: nextId('sac'), run_id: run.id, kind: 'story', status: 'open', version: 1, prompt: 'Want to add a story?', allowed_responses: ['submit_story', 'answer', 'skip'], options: [], candidate_memory_ids: [],
        timestamps: { ...stamps(), answered: null, expires: null }, images: null, preview: null }];
      return clone(touch(run));
    },
    succeed: (runId, script = 'AI script', cost = 100, suggestions = []) => {
      const run = db.runs.find(item => item.id === runId);
      const p = db.projects.find(item => item.id === run.project_id);
      const blocks = Array.isArray(script) ? script : [{ id: null, kind: 'opening', spoken_text: script, recording_notes: '' }];
      // The harness cites evidence of the brief's sourced topic.
      const evidence_ids = p.brief?.topic?.kind === 'sourced' ? p.brief.topic.evidence_ids : [];
      const revision = writeRevision(p, { title: p.title, blocks, evidence_ids }, 'ai', run.id, suggestions);
      message(p, 'assistant', 'progress', 'Your script is ready.', { run_id: run.id });
      settle(db, run.id, cost);
      run.status = 'succeeded'; run.phase = 'done'; run.result_revision_id = revision.id; run.outcome = 'revision'; p.active_run_id = null;
      return clone(touch(run));
    },
    // Contract 1.7.0: the message was a question, answered in the chat. No revision; it
    // pays only the tokens of its single model call.
    answer: (runId, text = 'Aim for 30 to 45 seconds.', cost = 10) => {
      const run = db.runs.find(item => item.id === runId);
      const p = db.projects.find(item => item.id === run.project_id);
      message(p, 'assistant', 'answer', text, { run_id: run.id });
      settle(db, run.id, cost);
      run.status = 'succeeded'; run.phase = 'done'; run.outcome = 'answer'; p.active_run_id = null;
      return clone(touch(run));
    },
    // The transcription worker: the audio's text, or a failure.
    transcribe: (transcriptionId, text = 'Transcribed audio', failed = false, cost = 5) => {
      const transcription = db.transcriptions.find(item => item.id === transcriptionId);
      if (!transcription || !['queued', 'running'].includes(transcription.status)) return clone(transcription);
      transcription.timestamps.started = transcription.timestamps.started || now();
      transcription.timestamps.completed = now();
      if (failed) {
        transcription.status = 'failed';
        transcription.error = { message: 'Provider down', code: 'transcription_failed', request_id: 'sreq-0000002', retryable: true, errors: {}, details: {}, timestamps: { occurred: now() } };
        settle(db, transcription.id, 0);
      } else {
        Object.assign(transcription, { status: 'ready', text });
        settle(db, transcription.id, cost);
      }
      return clone(touch(transcription));
    },
    // Payments: what Stripe and its webhook do. A paid session is fulfilled only by the webhook.
    seedSubscription: (sku = 'standard', overrides = {}) => {
      const subscription = startSubscription(sku, overrides.currency || 'brl', overrides);
      // A scheduled cancellation ends the access at the end of the cycle.
      if (subscription.cancel_at_period_end) Object.assign(subscription, { cancel_at: subscription.current_period_end, timestamps: { ...subscription.timestamps, canceled: now() } });
      return clone(subscriptionView());
    },
    upgradeOutcome: outcome => { db.payments.upgrade = outcome; },
    payCheckout: checkoutId => {
      const checkout = findCheckout(checkoutId);
      Object.assign(checkout, { status: 'complete', client_secret: null, expires_at: null });
      checkout.timestamps.updated = now();
      db.payments.paid[checkout.id] = now();
      return clone(checkout);
    },
    fulfillCheckout: checkoutId => {
      const checkout = findCheckout(checkoutId);
      if (checkout.fulfillment !== 'pending') return clone(checkout);
      if (checkout.kind === 'subscription') checkout.subscription_id = startSubscription(checkout.plan, checkout.currency).id;
      else {
        db.payments.purchased = balance().purchased + checkout.units;
        db.credits += checkout.units;
        db.payments.currency = db.payments.currency || checkout.currency;
        recordInvoice({ kind: 'credits', reason: 'manual', units: checkout.units, total: checkout.amount_total, currency: checkout.currency });
      }
      Object.assign(checkout, { status: 'complete', fulfillment: 'fulfilled', units_granted: checkout.units, client_secret: null, expires_at: null });
      checkout.timestamps.updated = now(); checkout.timestamps.fulfilled = now();
      return clone(checkout);
    },
    // The held upgrade's invoice gets paid (3DS confirmed, or the card was replaced).
    settleUpgrade: () => {
      const subscription = db.payments.subscription;
      if (!subscription?.pending_change) return clone(subscriptionView());
      const held = (db.payments.invoices || []).find(item => item.hosted_invoice_url === subscription.pending_change.payment_url);
      restartCycle(subscription, subscription.pending_change.plan);
      if (held) Object.assign(held, { status: 'paid', amount_paid: held.total, period_start: subscription.current_period_start, period_end: subscription.current_period_end,
        timestamps: { ...held.timestamps, paid: now() } });
      subscription.version += 1;
      return clone(subscriptionView());
    },
    // A pack bought before the mock's "now": its credits and its invoice.
    seedPack: (packs = 20, at = Date.now()) => {
      const units = packs * CREDIT_PACK.credits_per_pack;
      const currency = db.payments.currency || 'brl';
      db.payments.purchased = balance().purchased + units;
      db.credits += units;
      return clone(recordInvoice({ kind: 'credits', reason: 'manual', units, total: packs * amountIn(CREDIT_PACK.prices, currency), currency, at }));
    },
    failRun: (runId, retryable = true) => {
      const run = db.runs.find(item => item.id === runId);
      run.status = 'failed';
      run.error = { message: 'Provider down', code: 'provider_unavailable', request_id: 'sreq-0000001', retryable, errors: {}, details: {}, timestamps: { occurred: now() } };
      db.projects.find(item => item.id === run.project_id).active_run_id = null;
      return clone(touch(run));
    },
    // Personality worker. `stage` moves every source along; while transcribing, the first
    // `ready` of them are done, and from `comparing` (2.5.0, only with a sample) all of them.
    // A video transcribed by an earlier mapping is ready at once.
    advancePersonality: (mappingId, stage, ready = 0) => {
      const mapping = db.personalityMappings.find(item => item.id === mappingId);
      if (!mapping || !MAPPING_RUNNING.includes(mapping.status)) return clone(mapping);
      Object.assign(mapping, { status: 'running', stage });
      mapping.timestamps.started = mapping.timestamps.started || now();
      let done = 0;
      mapping.sources.forEach(source => {
        const known = db.transcribed[source.video_id];
        if (known !== undefined) Object.assign(source, { status: 'ready', words: known });
        else if (stage === 'analyzing' || stage === 'comparing' || (stage === 'transcribing' && done++ < ready)) Object.assign(source, { status: 'ready', words: sampleWords(source.duration_seconds) });
        else source.status = stage === 'downloading' ? 'downloading' : 'transcribing';
      });
      return clone(touch(mapping));
    },
    // Success: the result becomes the profile's personality and the operation is charged
    // what it cost (new transcriptions plus the analysis, and the comparison with a sample,
    // by default), never above the reservation. The sampled reels stay transcribed too.
    completePersonality: (mappingId, cost = null) => {
      const mapping = db.personalityMappings.find(item => item.id === mappingId);
      if (!mapping || !MAPPING_RUNNING.includes(mapping.status)) return clone(mapping);
      const profile = connected(creator()).find(item => item.id === mapping.profile_id);
      const sample = db.performanceSamples[mapping.id] !== undefined ? db.performanceSamples[mapping.id] : profile ? performanceSample(profile) : null;
      const sampledVideos = (sample?.videos || []).map(item => item.video).filter(video => !mapping.sources.some(source => source.video_id === video.id));
      const fresh = [...mapping.sources.map(source => ({ id: source.video_id, duration_seconds: source.duration_seconds })), ...sampledVideos].filter(video => db.transcribed[video.id] === undefined);
      const measured = Math.ceil(fresh.reduce((total, video) => total + video.duration_seconds / 60, 0) * TRANSCRIPTION_CREDITS_PER_MINUTE) + 44 + (sampled(sample) ? PERFORMANCE.cost : 0);
      const performance = samplePerformance(mapping.locale, sample, video => ({ url: video.cover, timestamps: { expires: later(MEDIA_TTL_MS) } }));
      sampledVideos.forEach(video => { db.transcribed[video.id] = db.transcribed[video.id] ?? sampleWords(video.duration_seconds); });
      mapping.sources.forEach(source => {
        const words = db.transcribed[source.video_id] ?? source.words ?? sampleWords(source.duration_seconds);
        db.transcribed[source.video_id] = words;
        Object.assign(source, { status: 'ready', words });
      });
      const completed = now();
      Object.assign(mapping, { status: 'completed', stage: null });
      mapping.timestamps.started = mapping.timestamps.started || completed;
      mapping.timestamps.completed = completed;
      const personality = { mapping_id: mapping.id, locale: mapping.locale, ...samplePersonality(mapping.locale, mapping.sources), performance, sources: clone(mapping.sources),
        timestamps: { mapped: completed } };
      db.personalities[mapping.profile_id] = { personality, updated: completed };
      settle(db, mapping.id, Math.min(cost ?? measured, db.charges[mapping.id] || 0));
      return clone(touch(mapping));
    },
    // Failure: nothing is charged and the previous personality stays. `failures` maps a
    // source's index to its code (`video_unavailable`, `video_out_of_range`,
    // `insufficient_speech`, `download_failed`, `provider_unavailable`): those fail alone,
    // the others are transcribed (and reused by the next mapping) and the mapping ends in
    // `sources_failed`. `null` = every video went through and the analysis failed.
    failPersonality: (mappingId, failures = { 1: 'insufficient_speech' }) => {
      const mapping = db.personalityMappings.find(item => item.id === mappingId);
      if (!mapping || !MAPPING_RUNNING.includes(mapping.status)) return clone(mapping);
      const errors = {};
      mapping.sources.forEach((source, index) => {
        const code = failures?.[index];
        if (!code) {
          const words = db.transcribed[source.video_id] ?? source.words ?? sampleWords(source.duration_seconds);
          db.transcribed[source.video_id] = words;
          Object.assign(source, { status: 'ready', words });
          return;
        }
        if (code === 'insufficient_speech') db.transcribed[source.video_id] = 4;
        Object.assign(source, { status: 'failed', error_code: code, words: code === 'insufficient_speech' ? 4 : null });
        errors[`video_ids.${index}`] = [code];
      });
      const count = Object.keys(errors).length;
      const error = count
        ? personalityError('sources_failed', `${count} of the ${mapping.sources.length} videos could not be used.`, Object.values(errors).every(([code]) => RETRYABLE.includes(code)), errors)
        : personalityError('provider_unavailable', 'The personality mapping failed.', true);
      Object.assign(mapping, { status: 'failed', stage: null, error });
      mapping.timestamps.failed = now();
      settle(db, mapping.id, 0);
      return clone(touch(mapping));
    },
    // An active personality mapped `daysAgo` from three of the profile's videos (their indexes).
    seedPersonality: (profileId = connected(creator())[0]?.id, picks = [0, 1, 3], daysAgo = 6) => {
      const profile = connected(creator()).find(item => item.id === profileId);
      const at = new Date(Date.now() - daysAgo * 864e5).toISOString();
      const mapping = { id: nextId('spm'), creator_id: creator().id, profile_id: profile.id, status: 'queued', stage: null, sources: picks.map(index => personalitySource(videosOf(profile)[index])),
        usage_operation_id: nextId('suo'), locale: profile.locale || 'pt_BR', error: null, version: 1, timestamps: { created: at, updated: at, started: at, completed: null, failed: null } };
      db.personalityMappings.push(mapping);
      api.server.completePersonality(mapping.id, 0);
      Object.assign(mapping.timestamps, { updated: at, completed: at });
      db.personalities[profile.id].updated = at;
      db.personalities[profile.id].personality.timestamps.mapped = at;
      return clone(db.personalities[profile.id].personality);
    },
    // What Meta lists for a profile, in place of the sample videos.
    seedVideos: (profileId, rows) => { db.videos[profileId] = rows; return clone(rows); },
  };
  return api;
}
